import React, { useState, useEffect, useRef, lazy, Suspense } from 'react'
import type { SwissPageData, SwissStat, SwissService, SwissWork, FreeElement } from '../../types/swissPage'
import { mergeSwissData } from '../../types/swissPage'
import type { BlockState } from '../../store/editor.store'

const MediaLibraryModal = lazy(() => import('../MediaLibraryModal').then(m => ({ default: m.MediaLibraryModal })))

interface Props {
  block: BlockState
  onChange: (key: string, value: unknown) => void
}

const VARIANTS = [
  { id: 'bst',    label: 'BST',    color: '#E4030E', bg: '#F7F6F2' },
  { id: 'nord',   label: 'NORD',   color: '#C1501F', bg: '#F1E9DC' },
  { id: 'klima',  label: 'KLIMA',  color: '#FF4D00', bg: '#F2F0E9' },
  { id: 'orbita', label: 'ORB',    color: '#3B5BFF', bg: '#0C0D10' },
] as const

// ── shared field styles ──────────────────────────────────────────────────────
const inp: React.CSSProperties = {
  width: '100%', padding: '6px 8px', fontSize: '12px',
  border: '1px solid var(--border)', borderRadius: '6px',
  background: 'var(--bg-2)', color: 'var(--text-primary)',
  fontFamily: 'inherit',
}
const label: React.CSSProperties = {
  fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', display: 'block',
}
const row: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '4px' }
const sectionHead: React.CSSProperties = {
  fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)',
  textTransform: 'uppercase', letterSpacing: '0.08em',
  padding: '10px 12px 4px', borderTop: '1px solid var(--border)',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
}

const INSERT_TYPES = [
  { type: 'button-outline', label: '⊡ Кнопка' },
  { type: 'button-solid', label: '■ Кнопка' },
  { type: 'heading', label: 'Aa Заголовок' },
  { type: 'text', label: 'ab Текст' },
] as const

function getGhostHtml(type: string): string {
  const s = 'display:inline-block;'
  if (type === 'button-outline') return `<a class="btn" style="${s}">Кнопка <span>→</span></a>`
  if (type === 'button-solid') return `<a class="btn solid" style="${s}">Кнопка <span>→</span></a>`
  if (type === 'heading') return `<h2 style="${s}font-weight:800;font-size:2rem;letter-spacing:-.02em;color:inherit;">Заголовок</h2>`
  return `<p style="${s}max-width:360px;">Текст абзаца</p>`
}

function getDefaultContent(type: string): string {
  if (type === 'button-outline' || type === 'button-solid') return 'Кнопка'
  if (type === 'heading') return 'Заголовок'
  return 'Текст'
}

function DeleteBtn({ onDelete }: { onDelete: () => void }) {
  return (
    <button
      onClick={onDelete}
      title="Удалить элемент"
      style={{
        fontSize: '10px', padding: '1px 6px', borderRadius: '4px', cursor: 'pointer',
        border: '1px solid var(--border)', background: 'transparent',
        color: 'var(--text-muted)', lineHeight: 1.4,
      }}
    >
      ✕
    </button>
  )
}

function RestoreBtn({ label, onRestore }: { label: string; onRestore: () => void }) {
  return (
    <button
      onClick={onRestore}
      style={{
        display: 'flex', alignItems: 'center', gap: '4px',
        padding: '3px 8px', fontSize: '11px', cursor: 'pointer',
        border: '1px solid var(--accent)', borderRadius: '4px',
        background: 'rgba(91,106,240,0.07)', color: 'var(--accent)', width: '100%',
      }}
    >
      + {label}
    </button>
  )
}

function Field({ label: lbl, children, action }: { label: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div style={row}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
        <span style={{ ...label, marginBottom: 0 }}>{lbl}</span>
        {action}
      </div>
      {children}
    </div>
  )
}

function SectionToggle({ enabled, onToggle }: { enabled: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      title={enabled ? 'Скрыть секцию' : 'Показать секцию'}
      style={{
        fontSize: '10px', padding: '2px 7px', borderRadius: '4px', cursor: 'pointer',
        border: `1px solid ${enabled ? 'var(--accent)' : 'var(--border)'}`,
        background: enabled ? 'var(--accent)' : 'var(--bg-3)',
        color: enabled ? '#fff' : 'var(--text-muted)', fontWeight: 600,
      }}
    >
      {enabled ? '✓ вкл' : 'откл'}
    </button>
  )
}

function Accordion({ title, enabled, onToggle, children, forceOpen }: { title: string; enabled?: boolean; onToggle?: () => void; children: React.ReactNode; forceOpen?: boolean }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (forceOpen && !open) {
      setOpen(true)
      setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50)
    }
  }, [forceOpen])

  return (
    <div ref={ref} style={{ borderBottom: '1px solid var(--border)' }}>
      <div style={{ ...sectionHead, cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <span>{title}</span>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {onToggle && (
            <div onClick={e => e.stopPropagation()}>
              <SectionToggle enabled={!!enabled} onToggle={onToggle} />
            </div>
          )}
          <span style={{ opacity: 0.5 }}>{open ? '▲' : '▼'}</span>
        </div>
      </div>
      {open && (
        <div style={{ padding: '8px 12px 12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {children}
        </div>
      )}
    </div>
  )
}

const FIELD_TO_SECTION: Record<string, string> = {
  heroTitle: 'hero', heroKicker: 'hero', heroTagline: 'hero',
  heroImage: 'hero', heroBtn1: 'hero', heroBtn2: 'hero',
  brand: 'brand',
  manifest: 'manifest',
  stats: 'stats',
  services: 'services',
  works: 'works',
  quote: 'quote',
  contact: 'contact',
}

export function SwissPageProperties({ block, onChange }: Props) {
  const data: SwissPageData = mergeSwissData(block.data as Record<string, unknown>)
  const showTitle = data.showHeroTitle as [boolean, boolean, boolean]
  const [mediaOpen, setMediaOpen] = useState(false)
  const [focusSection, setFocusSection] = useState<string | null>(null)
  const [insertMode, setInsertMode] = useState<string | null>(null)

  // Refs to avoid stale closures in event listeners
  const latestData = useRef(data)
  const latestOnChange = useRef(onChange)
  latestData.current = data
  latestOnChange.current = onChange

  // Listen for element placement and cancellation from canvas iframe
  useEffect(() => {
    const onPlaced = (e: Event) => {
      const d = (e as CustomEvent<{ blockId: string; elementType: string; pageX: number; pageY: number }>).detail
      if (d.blockId !== block.id) return
      const el: FreeElement = {
        id: `f${Date.now()}`,
        type: d.elementType as FreeElement['type'],
        content: getDefaultContent(d.elementType),
        x: d.pageX,
        y: d.pageY,
      }
      latestOnChange.current('freeElements', [...(latestData.current.freeElements ?? []), el])
      setInsertMode(null)
    }
    const onCancelled = (e: Event) => {
      const d = (e as CustomEvent<{ blockId: string }>).detail
      if (d.blockId === block.id) setInsertMode(null)
    }
    window.addEventListener('bst-swiss-element-placed', onPlaced as EventListener)
    window.addEventListener('bst-swiss-insert-cancelled', onCancelled as EventListener)
    return () => {
      window.removeEventListener('bst-swiss-element-placed', onPlaced as EventListener)
      window.removeEventListener('bst-swiss-insert-cancelled', onCancelled as EventListener)
    }
  }, [block.id])

  function startInsert(type: string) {
    if (insertMode === type) {
      setInsertMode(null)
      window.dispatchEvent(new CustomEvent('bst-swiss-cancel-insert', { detail: { blockId: block.id } }))
      return
    }
    setInsertMode(type)
    window.dispatchEvent(new CustomEvent('bst-swiss-insert-start', {
      detail: { blockId: block.id, elementType: type, ghostHtml: getGhostHtml(type) }
    }))
  }

  useEffect(() => {
    const handler = (e: Event) => {
      const field = (e as CustomEvent<string>).detail
      const section = FIELD_TO_SECTION[field]
      if (section) {
        setFocusSection(null) // reset first so useEffect in Accordion fires again for same section
        requestAnimationFrame(() => setFocusSection(section))
      }
    }
    window.addEventListener('swiss-field-click', handler)
    return () => window.removeEventListener('swiss-field-click', handler)
  }, [])

  function set<K extends keyof SwissPageData>(key: K, value: SwissPageData[K]) {
    onChange(key, value)
  }

  function setStat(i: number, field: keyof SwissStat, value: string | number) {
    const next = data.stats.map((s, idx) => idx === i ? { ...s, [field]: field === 'value' ? Number(value) : value } : s)
    set('stats', next)
  }

  function setService(i: number, field: keyof SwissService, value: string) {
    const next = data.services.map((s, idx) => idx === i ? { ...s, [field]: value } : s)
    set('services', next)
  }

  function setWork(i: number, patch: Partial<SwissWork>) {
    const next = data.works.map((w, idx) => idx === i ? { ...w, ...patch } as SwissWork : w)
    set('works', next)
  }

  function addWork() {
    set('works', [...data.works, { k: 'type', w: 'СЛОВО', m: 'Описание', v: '', s: 's5' } as SwissWork])
  }

  function removeWork(i: number) {
    set('works', data.works.filter((_, idx) => idx !== i))
  }

  return (
    <div>

      {/* Insert toolbar */}
      <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', background: insertMode ? 'rgba(91,106,240,0.05)' : undefined }}>
        {insertMode ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '11px', color: 'var(--accent)', fontWeight: 600, flex: 1 }}>
              👆 Кликните на шаблон куда поставить
            </span>
            <button
              onClick={() => { setInsertMode(null); window.dispatchEvent(new CustomEvent('bst-swiss-cancel-insert', { detail: { blockId: block.id } })) }}
              style={{ padding: '2px 8px', fontSize: '10px', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer', background: 'var(--bg-2)', color: 'var(--text-muted)', flexShrink: 0 }}
            >
              Отмена
            </button>
          </div>
        ) : (
          <>
            <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '7px' }}>
              Вставить элемент
            </div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {INSERT_TYPES.map(item => (
                <button
                  key={item.type}
                  onClick={() => startInsert(item.type)}
                  style={{ padding: '4px 9px', fontSize: '11px', cursor: 'pointer', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg-3)', color: 'var(--text-primary)' }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Theme */}
      <div style={{ padding: '12px' }}>
        <div style={{ ...label, marginBottom: '8px' }}>Цветовая тема</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
          {VARIANTS.map(v => (
            <button
              key={v.id}
              onClick={() => set('variant', v.id)}
              style={{
                padding: '8px 4px', borderRadius: '6px', cursor: 'pointer', fontSize: '10px',
                fontWeight: 700, letterSpacing: '0.06em',
                border: `2px solid ${data.variant === v.id ? v.color : 'var(--border)'}`,
                background: v.bg, color: data.variant === v.id ? v.color : '#666',
              }}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Brand identity */}
      <Accordion title="🏢 Бренд" forceOpen={focusSection === 'brand'}>
        <Field label="Название (краткое)">
          <input style={inp} value={data.brandName} onChange={e => set('brandName', e.target.value)} placeholder="BST" />
        </Field>
        <Field label="Полное название">
          <input style={inp} value={data.brandFull} onChange={e => set('brandFull', e.target.value)} placeholder="Bureau Swiss Type" />
        </Field>
        <Field label="Город">
          <input style={inp} value={data.city} onChange={e => set('city', e.target.value.toUpperCase())} placeholder="МОСКВА" />
        </Field>
        <Field label="Гео-координаты">
          <input style={inp} value={data.geo} onChange={e => set('geo', e.target.value)} placeholder="55.75° N — 37.61° E" />
        </Field>
      </Accordion>

      {/* Hero */}
      <Accordion title="🦸 Герой" forceOpen={focusSection === 'hero'}>
        {data.showHeroKicker && (
          <Field label="Кикер" action={<DeleteBtn onDelete={() => set('showHeroKicker', false)} />}>
            <input style={inp} value={data.heroKicker} onChange={e => set('heroKicker', e.target.value)} />
          </Field>
        )}
        {showTitle[0] && (
          <Field label="Заголовок — строка 1" action={<DeleteBtn onDelete={() => set('showHeroTitle', [false, showTitle[1], showTitle[2]])} />}>
            <input style={inp} value={data.heroTitle[0]} onChange={e => set('heroTitle', [e.target.value, data.heroTitle[1], data.heroTitle[2]])} />
          </Field>
        )}
        {showTitle[1] && (
          <Field label="Заголовок — строка 2" action={<DeleteBtn onDelete={() => set('showHeroTitle', [showTitle[0], false, showTitle[2]])} />}>
            <input style={inp} value={data.heroTitle[1]} onChange={e => set('heroTitle', [data.heroTitle[0], e.target.value, data.heroTitle[2]])} />
          </Field>
        )}
        {showTitle[2] && (
          <Field label="Заголовок — строка 3" action={<DeleteBtn onDelete={() => set('showHeroTitle', [showTitle[0], showTitle[1], false])} />}>
            <input style={inp} value={data.heroTitle[2]} onChange={e => set('heroTitle', [data.heroTitle[0], data.heroTitle[1], e.target.value])} />
          </Field>
        )}
        {data.showHeroTagline && (
          <Field label="Описание (tagline)" action={<DeleteBtn onDelete={() => set('showHeroTagline', false)} />}>
            <textarea style={{ ...inp, resize: 'vertical', minHeight: '64px' }} value={data.heroTagline} onChange={e => set('heroTagline', e.target.value)} />
          </Field>
        )}
        {data.showHeroImage && (
          <>
            <Field label="Изображение" action={<DeleteBtn onDelete={() => set('showHeroImage', false)} />}>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input style={{ ...inp, flex: 1 }} value={data.heroImage} onChange={e => set('heroImage', e.target.value)} placeholder="https://..." />
                <button
                  onClick={() => setMediaOpen(true)}
                  title="Выбрать из медиатеки"
                  style={{ padding: '5px 8px', border: '1px solid var(--border)', borderRadius: '6px', background: 'var(--bg-3)', cursor: 'pointer', fontSize: '14px', flexShrink: 0 }}
                >🖼</button>
              </div>
              {data.heroImage && (
                <img src={data.heroImage} alt="" style={{ width: '100%', height: '80px', objectFit: 'cover', borderRadius: '4px', marginTop: '4px' }} />
              )}
            </Field>
            <Field label="Подпись под фото">
              <input style={inp} value={data.heroCap} onChange={e => set('heroCap', e.target.value)} />
            </Field>
          </>
        )}
        {mediaOpen && (
          <Suspense fallback={null}>
            <MediaLibraryModal
              onSelect={url => { set('heroImage', url); setMediaOpen(false) }}
              onClose={() => setMediaOpen(false)}
            />
          </Suspense>
        )}
        {data.showHeroBtn1 && (
          <div style={{ padding: '8px', background: 'var(--bg-1)', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)' }}>Элемент 1</span>
              <DeleteBtn onDelete={() => set('showHeroBtn1', false)} />
            </div>
            <select style={inp} value={data.heroBtn1Type} onChange={e => set('heroBtn1Type', e.target.value as SwissPageData['heroBtn1Type'])}>
              <option value="btn-outline">Кнопка (контур)</option>
              <option value="btn-solid">Кнопка (залитая)</option>
              <option value="link">Ссылка-текст</option>
              <option value="heading">Заголовок</option>
            </select>
            <input style={inp} value={data.heroBtn1} onChange={e => set('heroBtn1', e.target.value)} placeholder="Смотреть работы" />
          </div>
        )}
        {data.showHeroBtn2 && (
          <div style={{ padding: '8px', background: 'var(--bg-1)', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)' }}>Элемент 2</span>
              <DeleteBtn onDelete={() => set('showHeroBtn2', false)} />
            </div>
            <select style={inp} value={data.heroBtn2Type} onChange={e => set('heroBtn2Type', e.target.value as SwissPageData['heroBtn2Type'])}>
              <option value="btn-outline">Кнопка (контур)</option>
              <option value="btn-solid">Кнопка (залитая)</option>
              <option value="link">Ссылка-текст</option>
              <option value="heading">Заголовок</option>
            </select>
            <input style={inp} value={data.heroBtn2} onChange={e => set('heroBtn2', e.target.value)} placeholder="Начать проект" />
          </div>
        )}

        {/* Restore deleted elements */}
        {(!data.showHeroKicker || !showTitle[0] || !showTitle[1] || !showTitle[2] ||
          !data.showHeroTagline || !data.showHeroImage || !data.showHeroBtn1 || !data.showHeroBtn2) && (
          <div style={{ border: '1px dashed var(--border)', borderRadius: '6px', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '2px' }}>Удалённые элементы</div>
            {!data.showHeroKicker && <RestoreBtn label="Кикер" onRestore={() => set('showHeroKicker', true)} />}
            {!showTitle[0] && <RestoreBtn label="Заголовок — строка 1" onRestore={() => set('showHeroTitle', [true, showTitle[1], showTitle[2]])} />}
            {!showTitle[1] && <RestoreBtn label="Заголовок — строка 2" onRestore={() => set('showHeroTitle', [showTitle[0], true, showTitle[2]])} />}
            {!showTitle[2] && <RestoreBtn label="Заголовок — строка 3" onRestore={() => set('showHeroTitle', [showTitle[0], showTitle[1], true])} />}
            {!data.showHeroTagline && <RestoreBtn label="Описание" onRestore={() => set('showHeroTagline', true)} />}
            {!data.showHeroImage && <RestoreBtn label="Изображение" onRestore={() => set('showHeroImage', true)} />}
            {!data.showHeroBtn1 && <RestoreBtn label="Элемент 1" onRestore={() => set('showHeroBtn1', true)} />}
            {!data.showHeroBtn2 && <RestoreBtn label="Элемент 2" onRestore={() => set('showHeroBtn2', true)} />}
          </div>
        )}
      </Accordion>

      {/* Marquee */}
      <Accordion title="📢 Бегущая строка">
        <Field label="Текст (через · )">
          <input style={inp} value={data.marqueeClaim} onChange={e => set('marqueeClaim', e.target.value)} placeholder="ДИЗАЙН · СТРАТЕГИЯ · СИСТЕМА" />
        </Field>
      </Accordion>

      {/* Manifest */}
      <Accordion
        title="📖 Манифест"
        enabled={data.showManifest}
        onToggle={() => set('showManifest', !data.showManifest)}
        forceOpen={focusSection === 'manifest'}
      >
        <Field label="Год основания">
          <input style={inp} value={data.foundedYear} onChange={e => set('foundedYear', e.target.value)} placeholder="2019" />
        </Field>
        <Field label="Абзац 1 (можно <b>жирный</b>)">
          <textarea style={{ ...inp, resize: 'vertical', minHeight: '80px' }} value={data.manifestText1} onChange={e => set('manifestText1', e.target.value)} />
        </Field>
        <Field label="Абзац 2">
          <textarea style={{ ...inp, resize: 'vertical', minHeight: '80px' }} value={data.manifestText2} onChange={e => set('manifestText2', e.target.value)} />
        </Field>
      </Accordion>

      {/* Stats */}
      <Accordion
        title="📊 Статистика"
        enabled={data.showStats}
        onToggle={() => set('showStats', !data.showStats)}
        forceOpen={focusSection === 'stats'}
      >
        {data.stats.map((s, i) => (
          <div key={i} style={{ padding: '8px', background: 'var(--bg-1)', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)' }}>Стат {i + 1}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 1fr', gap: '6px' }}>
              <div style={row}>
                <span style={label}>Число</span>
                <input style={inp} type="number" value={s.value} onChange={e => setStat(i, 'value', e.target.value)} />
              </div>
              <div style={row}>
                <span style={label}>Суффикс</span>
                <input style={inp} value={s.suffix} onChange={e => setStat(i, 'suffix', e.target.value)} placeholder="т / %" />
              </div>
              <div style={row}>
                <span style={label}>Подпись</span>
                <input style={inp} value={s.label} onChange={e => setStat(i, 'label', e.target.value)} />
              </div>
            </div>
          </div>
        ))}
      </Accordion>

      {/* Services */}
      <Accordion
        title="🛠 Услуги"
        enabled={data.showServices}
        onToggle={() => set('showServices', !data.showServices)}
        forceOpen={focusSection === 'services'}
      >
        {data.services.map((s, i) => (
          <div key={i} style={{ padding: '8px', background: 'var(--bg-1)', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)' }}>Услуга {i + 1}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '36px 1fr', gap: '6px' }}>
              <div style={row}>
                <span style={label}>№</span>
                <input style={inp} value={s.num} onChange={e => setService(i, 'num', e.target.value)} />
              </div>
              <div style={row}>
                <span style={label}>Название</span>
                <input style={inp} value={s.title} onChange={e => setService(i, 'title', e.target.value)} />
              </div>
            </div>
            <div style={row}>
              <span style={label}>Описание</span>
              <textarea style={{ ...inp, resize: 'vertical', minHeight: '56px' }} value={s.desc} onChange={e => setService(i, 'desc', e.target.value)} />
            </div>
          </div>
        ))}
      </Accordion>

      {/* Works */}
      <Accordion
        title="🖼 Работы / портфолио"
        enabled={data.showWorks}
        onToggle={() => set('showWorks', !data.showWorks)}
        forceOpen={focusSection === 'works'}
      >
        {data.works.map((w, i) => (
          <div key={i} style={{ padding: '8px', background: 'var(--bg-1)', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <select
                style={{ ...inp, width: 'auto' }}
                value={w.k}
                onChange={e => {
                  const k = e.target.value as SwissWork['k']
                  if (k === 'img') setWork(i, { k, src: '', t: '', m: '', s: 's5' })
                  else if (k === 'type') setWork(i, { k, w: '', m: '', v: '', s: 's5' })
                  else setWork(i, { k, m: '', list: [''], s: 's5' })
                }}
              >
                <option value="img">📷 Фото</option>
                <option value="type">🔤 Текстовая плитка</option>
                <option value="idx">📋 Список</option>
              </select>
              <button
                onClick={() => removeWork(i)}
                style={{ padding: '3px 8px', background: 'none', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', color: 'var(--text-muted)' }}
              >✕</button>
            </div>

            {w.k === 'img' && (
              <>
                <div style={row}><span style={label}>URL фото</span><input style={inp} value={w.src} onChange={e => setWork(i, { src: e.target.value })} placeholder="https://..." /></div>
                <div style={row}><span style={label}>Название</span><input style={inp} value={w.t} onChange={e => setWork(i, { t: e.target.value })} /></div>
                <div style={row}><span style={label}>Подпись</span><input style={inp} value={w.m} onChange={e => setWork(i, { m: e.target.value })} /></div>
              </>
            )}
            {w.k === 'type' && (
              <>
                <div style={row}><span style={label}>Слово/аббревиатура</span><input style={inp} value={w.w} onChange={e => setWork(i, { w: e.target.value })} /></div>
                <div style={row}><span style={label}>Подпись</span><input style={inp} value={w.m} onChange={e => setWork(i, { m: e.target.value })} /></div>
                <div style={row}>
                  <span style={label}>Цвет фона</span>
                  <select style={inp} value={w.v} onChange={e => setWork(i, { v: e.target.value })}>
                    <option value="">Светлый (по умолчанию)</option>
                    <option value="acc">Акцент</option>
                    <option value="ink">Тёмный</option>
                  </select>
                </div>
              </>
            )}
            {w.k === 'idx' && (
              <>
                <div style={row}><span style={label}>Заголовок списка</span><input style={inp} value={w.m} onChange={e => setWork(i, { m: e.target.value })} /></div>
                {w.list.map((li, li_i) => (
                  <div key={li_i} style={{ display: 'flex', gap: '4px' }}>
                    <input style={{ ...inp, flex: 1 }} value={li} onChange={e => {
                      const next = w.list.map((x, xi) => xi === li_i ? e.target.value : x)
                      setWork(i, { list: next })
                    }} />
                    {w.list.length > 1 && (
                      <button onClick={() => setWork(i, { list: w.list.filter((_, xi) => xi !== li_i) })}
                        style={{ padding: '0 8px', background: 'none', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer', color: 'var(--text-muted)' }}>✕</button>
                    )}
                  </div>
                ))}
                <button onClick={() => setWork(i, { list: [...w.list, ''] })}
                  style={{ ...inp, cursor: 'pointer', textAlign: 'center', color: 'var(--accent)' }}>+ Добавить пункт</button>
              </>
            )}

            <div style={row}>
              <span style={label}>Размер плитки</span>
              <select style={inp} value={w.s} onChange={e => setWork(i, { s: e.target.value })}>
                <option value="s4">Малый (4/12)</option>
                <option value="s5">Средний (5/12)</option>
                <option value="s6">Половина (6/12)</option>
                <option value="s7">Большой (7/12)</option>
                <option value="s8">Широкий (8/12)</option>
                <option value="s12">Полный (12/12)</option>
              </select>
            </div>
          </div>
        ))}
        <button
          onClick={addWork}
          style={{ ...inp, cursor: 'pointer', textAlign: 'center', color: 'var(--accent)', fontWeight: 600 }}
        >+ Добавить карточку</button>
      </Accordion>

      {/* Quote */}
      <Accordion
        title="💬 Цитата"
        enabled={data.showQuote}
        onToggle={() => set('showQuote', !data.showQuote)}
        forceOpen={focusSection === 'quote'}
      >
        <Field label="Текст цитаты">
          <textarea style={{ ...inp, resize: 'vertical', minHeight: '72px' }} value={data.quote} onChange={e => set('quote', e.target.value)} />
        </Field>
        <Field label="Автор">
          <input style={inp} value={data.quoteAuthor} onChange={e => set('quoteAuthor', e.target.value)} />
        </Field>
      </Accordion>

      {/* Contact */}
      <Accordion
        title="📬 Контакт"
        enabled={data.showContact}
        onToggle={() => set('showContact', !data.showContact)}
        forceOpen={focusSection === 'contact'}
      >
        <Field label="Email">
          <input style={inp} type="email" value={data.email} onChange={e => set('email', e.target.value)} placeholder="hello@example.com" />
        </Field>
        <Field label="Примечание под email">
          <textarea style={{ ...inp, resize: 'vertical', minHeight: '64px' }} value={data.contactNote} onChange={e => set('contactNote', e.target.value)} />
        </Field>
        <Field label="Адрес — строка 1">
          <input style={inp} value={data.address[0] ?? ''} onChange={e => set('address', [e.target.value, data.address[1] ?? ''])} />
        </Field>
        <Field label="Адрес — строка 2">
          <input style={inp} value={data.address[1] ?? ''} onChange={e => set('address', [data.address[0] ?? '', e.target.value])} />
        </Field>
        <Field label="Телефон">
          <input style={inp} value={data.phone} onChange={e => set('phone', e.target.value)} placeholder="+7 000 000-00-00" />
        </Field>
      </Accordion>

      {/* Free elements management */}
      {(data.freeElements ?? []).length > 0 && (
        <Accordion title={`⊕ Вставленные элементы (${data.freeElements!.length})`}>
          {data.freeElements!.map((el, i) => (
            <div key={el.id} style={{ padding: '6px', background: 'var(--bg-1)', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)' }}>
                  {el.type === 'button-outline' ? 'Кнопка (контур)' : el.type === 'button-solid' ? 'Кнопка (залитая)' : el.type === 'heading' ? 'Заголовок' : 'Текст'}
                </span>
                <button
                  onClick={() => onChange('freeElements', data.freeElements!.filter((_, j) => j !== i))}
                  style={{ padding: '1px 6px', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer', background: 'transparent', color: 'var(--text-muted)', fontSize: '10px' }}
                >✕</button>
              </div>
              <input
                style={inp}
                value={el.content}
                onChange={e => {
                  const next = data.freeElements!.map((fe, j) => j === i ? { ...fe, content: e.target.value } : fe)
                  onChange('freeElements', next)
                }}
              />
            </div>
          ))}
        </Accordion>
      )}

      <div style={{ height: '60px' }} />
    </div>
  )
}
